import React from "react";
import { Check, AlertTriangle, Plus, ChevronDown, ChevronUp, Shield, Star, Scale, Lightbulb, Brain } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

const RATING_COLORS = {
  strong: "bg-emerald-100 text-emerald-700",
  adequate: "bg-blue-100 text-blue-700",
  needs_improvement: "bg-amber-100 text-amber-700",
  weak: "bg-red-100 text-red-700",
  missing: "bg-slate-100 text-slate-500",
};

const PRIORITY_COLORS = {
  critical: "bg-red-600 text-white",
  high: "bg-red-100 text-red-700",
  medium: "bg-amber-100 text-amber-700",
  low: "bg-slate-100 text-slate-600",
};

const CATEGORY_ICONS = {
  clarity: Lightbulb, enforceability: Shield, completeness: Plus,
  balance: Scale, compliance: Shield, risk: AlertTriangle,
};

function ClauseReviewItem({ cr, isExpanded, onToggle, onApplySuggestion }) {
  const Icon = CATEGORY_ICONS[cr.category] || AlertTriangle;
  return (
    <div className="border rounded-sm overflow-hidden" data-testid={`clause-review-${cr.id}`}>
      <button className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50 transition-colors" onClick={onToggle}>
        <Icon size={13} className="shrink-0 text-slate-400" />
        <span className="text-xs font-medium flex-1 truncate">{cr.section_name}</span>
        <Badge className={`rounded-sm text-[9px] ${RATING_COLORS[cr.rating]}`}>{cr.rating?.replace("_", " ")}</Badge>
        <Badge className={`rounded-sm text-[9px] ${PRIORITY_COLORS[cr.priority]}`}>{cr.priority}</Badge>
        {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>
      {isExpanded && (
        <div className="px-3 pb-3 space-y-2 border-t bg-slate-50/50">
          <p className="text-xs text-slate-600 mt-2">{cr.finding}</p>
          <div className="bg-violet-50 border border-violet-100 rounded-sm p-2">
            <p className="text-[10px] font-mono uppercase text-violet-500 mb-1">Suggestion</p>
            <p className="text-xs text-violet-700">{cr.suggestion}</p>
          </div>
          {cr.suggested_text && (
            <div className="bg-emerald-50 border border-emerald-100 rounded-sm p-2">
              <p className="text-[10px] font-mono uppercase text-emerald-500 mb-1">Suggested Text</p>
              <p className="text-xs text-emerald-800 whitespace-pre-wrap">{cr.suggested_text}</p>
              {onApplySuggestion && (
                <Button size="sm" variant="outline" className="mt-2 h-6 text-[10px] gap-1 rounded-sm border-emerald-300 text-emerald-700"
                  onClick={() => onApplySuggestion(cr.section_name, cr.suggested_text)} data-testid={`apply-suggestion-${cr.id}`}>
                  <Check size={10} /> Apply
                </Button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function ReviewQualityTab({ result, expandedClause, setExpandedClause, activeSection, setActiveSection, onApplySuggestion, onRerun }) {
  if (!result) return null;

  const clauseReviews = result.clause_reviews || [];
  const newClauses = result.new_clauses_recommended || [];
  const negotiationPts = result.negotiation_points || [];
  const criticalCount = clauseReviews.filter(c => c.priority === "critical" || c.priority === "high").length;

  return (
    <>
      {/* Score header */}
      <div className="flex items-center gap-3 p-3 rounded-sm bg-gradient-to-r from-violet-50 to-white border border-violet-100" data-testid="review-score">
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-lg font-bold ${
          result.overall_score >= 7 ? "bg-emerald-100 text-emerald-700" :
          result.overall_score >= 5 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"
        }`}>{result.overall_score}/10</div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-700">Contract Quality Score</p>
          <p className="text-xs text-slate-500 mt-0.5">{result.executive_summary}</p>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {criticalCount > 0 && <Badge className="bg-red-50 text-red-700 border-red-200 rounded-sm text-[10px]">{criticalCount} critical/high</Badge>}
        {newClauses.length > 0 && <Badge className="bg-violet-50 text-violet-700 border-violet-200 rounded-sm text-[10px]">{newClauses.length} new clause{newClauses.length > 1 ? "s" : ""}</Badge>}
        {negotiationPts.length > 0 && <Badge className="bg-blue-50 text-blue-700 border-blue-200 rounded-sm text-[10px]">{negotiationPts.length} negotiation pt{negotiationPts.length > 1 ? "s" : ""}</Badge>}
      </div>

      {/* Section tabs */}
      <div className="flex gap-1 border-b pb-1">
        {[
          { id: "clauses", label: `Clauses (${clauseReviews.length})` },
          { id: "new", label: `New (${newClauses.length})` },
          ...(negotiationPts.length > 0 ? [{ id: "negotiate", label: `Negotiate (${negotiationPts.length})` }] : []),
          { id: "strengths", label: "Strengths" },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveSection(tab.id)}
            className={`px-2.5 py-1 text-xs rounded-sm transition-colors ${activeSection === tab.id ? "bg-violet-100 text-violet-700 font-medium" : "text-slate-500 hover:bg-slate-50"}`}
            data-testid={`review-tab-${tab.id}`}>{tab.label}</button>
        ))}
      </div>

      {/* Clause Reviews */}
      {activeSection === "clauses" && (
        <div className="space-y-1.5" data-testid="clause-reviews">
          {clauseReviews.map(cr => (
            <ClauseReviewItem key={cr.id} cr={cr} isExpanded={expandedClause === cr.id}
              onToggle={() => setExpandedClause(expandedClause === cr.id ? null : cr.id)} onApplySuggestion={onApplySuggestion} />
          ))}
        </div>
      )}

      {/* New Clauses */}
      {activeSection === "new" && (
        <div className="space-y-2" data-testid="new-clauses">
          {newClauses.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-4">No new clauses recommended.</p>
          ) : newClauses.map(nc => (
            <div key={nc.id} className="border rounded-sm p-3 space-y-2" data-testid={`new-clause-${nc.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5"><Plus size={12} className="text-violet-500" />{nc.clause_name}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">Triggered by: {nc.trigger}</p>
                </div>
                <Badge className={`rounded-sm text-[9px] ${PRIORITY_COLORS[nc.priority]}`}>{nc.priority}</Badge>
              </div>
              <p className="text-xs text-slate-600">{nc.reason}</p>
              {nc.suggested_text && (
                <div className="bg-emerald-50 border border-emerald-100 rounded-sm p-2">
                  <p className="text-[10px] font-mono uppercase text-emerald-500 mb-1">Suggested Clause Text</p>
                  <p className="text-xs text-emerald-800 whitespace-pre-wrap">{nc.suggested_text}</p>
                  {onApplySuggestion && (
                    <Button size="sm" variant="outline" className="mt-2 h-6 text-[10px] gap-1 rounded-sm border-emerald-300 text-emerald-700"
                      onClick={() => onApplySuggestion(nc.clause_name, nc.suggested_text, true)} data-testid={`add-clause-${nc.id}`}>
                      <Plus size={10} /> Add Clause
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Negotiation Points */}
      {activeSection === "negotiate" && (
        <div className="space-y-2" data-testid="negotiation-points">
          {negotiationPts.map(np => (
            <div key={np.id} className="border rounded-sm p-3 space-y-2" data-testid={`negotiate-${np.id}`}>
              <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5"><Scale size={12} className="text-blue-500" />{np.point}</p>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-red-50 rounded-sm p-2"><p className="text-[10px] font-mono uppercase text-red-400 mb-0.5">Current</p><p className="text-xs text-red-700">{np.current_position}</p></div>
                <div className="bg-emerald-50 rounded-sm p-2"><p className="text-[10px] font-mono uppercase text-emerald-400 mb-0.5">Recommended</p><p className="text-xs text-emerald-700">{np.recommended_position}</p></div>
              </div>
              <p className="text-[10px] text-slate-500"><span className="font-medium">Leverage:</span> {np.leverage}</p>
            </div>
          ))}
        </div>
      )}

      {/* Strengths */}
      {activeSection === "strengths" && (
        <div className="space-y-1.5" data-testid="review-strengths">
          {(result.strengths || []).map((s, i) => (
            <div key={`str-${i}-${s?.slice(0,15)}`} className="flex items-start gap-2 px-2 py-1.5">
              <Star size={12} className="text-emerald-500 mt-0.5 shrink-0" /><p className="text-xs text-slate-600">{s}</p>
            </div>
          ))}
          {result.jurisdiction_notes && (
            <div className="mt-3 p-2.5 bg-blue-50 border border-blue-100 rounded-sm">
              <p className="text-[10px] font-mono uppercase text-blue-500 mb-1">Jurisdiction Notes</p>
              <p className="text-xs text-blue-700">{result.jurisdiction_notes}</p>
            </div>
          )}
        </div>
      )}

      <Button onClick={onRerun} variant="outline" size="sm" className="w-full gap-1.5 rounded-sm text-xs mt-2" data-testid="rerun-review">
        <Brain size={12} /> Re-run Review
      </Button>
    </>
  );
}
